import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ProductDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import YellowAccentLines from "@SupplyLand/Icons/YellowAccentLines";
import loadPromoCategoryProducts from "@SupplyLand/Store/Components/CustomPromotions/Handlers/LoadPromoCategoryProducts";
import { CustomState } from "@SupplyLand/Store/Components/CustomPromotions/PromoCategoryReducer";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const enum fields {
    titleText = "titleText",
    promoPriceLabel = "promoPriceLabel",
    imageUrl = "imageUrl",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.titleText]: string;
        [fields.imageUrl]: string;
        [fields.promoPriceLabel]: string;
    };
}
interface PromoInfo {
    Name: string;
    Product: ProductDto;
}

const mapStateToProps = (state: CustomState, ownProps: OwnProps) => {
    return {
        products: state.promoCategoryData?.products,
        fields: ownProps.fields,
    };
};

const mapDispatchToProps = {
    loadPromoCategoryProducts,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CategoryDetailPromoCategoriesStyles {
    titleWrapper?: InjectableCss;
    titleText?: TypographyPresentationProps;
    container?: GridContainerProps;
    promoCategoryItem?: GridItemProps;
    promoCategoryContainer?: GridContainerProps;
    promoCategoryGroup?: GridItemProps;
    innerContainer?: GridContainerProps;
    promoCategoryImageItem?: GridItemProps;
    promoCategoryImageClickable?: ClickablePresentationProps;
    promoCategoryImage?: LazyImageProps;
    promoCategorySelection?: InjectableCss;
    iconYellowAccentCard?: InjectableCss;
    categoryCardWrapper?: InjectableCss;
    tierLinkText?: InjectableCss;
}

const IconYellowAccentCard = styled.div<{}>`
    display: none;
    height: 0px;
`;

export const categoryDetailPromoCategoriesStyles: CategoryDetailPromoCategoriesStyles = {
    container: {
        gap: 0,
    },
    promoCategoryItem: {
        width: 12,
        css: css`
            padding-bottom: 60px;
            align-items: center;
            flex-direction: column;
            justify-content: space-between;
            flex: 0 1 100%;
            height: 100%;
        `,
    },
    innerContainer: {
        gap: 0,
    },
    promoCategoryImageItem: {
        width: [4, 4, 12, 12, 12],
        css: css`
            padding: 0 8px 8px 8px;
        `,
    },
    promoCategoryImageClickable: {
        css: css`
            width: 100%;
            height: 100%;
            justify-content: center;
            margin-bottom: 18px;
        `,
    },
    promoCategoryImage: {
        css: css`
            img {
                height: 200px;
                background-color: transparent;
                padding: 0;
                width: 225px;
            }
        `,
    },
    promoCategorySelection: {
        css: css`
            -moz-border-radius: 15px; //for mozilla support
            -webkit-border-radius: 15px; //for chrome support
            border-radius: 15px;
            background: #ffd100;
            padding-left: 40px;
            padding-right: 40px;
            font-weight: 700;
            font-size: 28px;
            color: rgba(0, 0, 0, 0.94);
            border-radius: 40px;
            padding-top: 5px;
            padding-bottom: 5px;
        `,
    },
    iconYellowAccentCard: {
        css: css`
            position: relative;
            top: -35px;
            justify-content: center;
            width: 100%;
        `,
    },
    categoryCardWrapper: {
        css: css`
        width:100%;
        :hover > ${IconYellowAccentCard}{
            display: block;
        },
        `,
    },
    tierLinkText: {
        css: css`
            display: block;
            text-align: center;
        `,
    },
};

const styles = categoryDetailPromoCategoriesStyles;

const PromoCategorySelection = ({ fields }: Props) => {
    const { titleText, promoPriceLabel, imageUrl } = fields;

    const renderCategoryCard = () => {
        return (
            <StyledWrapper
                css={css`
                    margin-bottom: 40px;
                `}
            >
                <Clickable href={`#${titleText}`} {...styles.promoCategoryImageClickable}>
                    <LazyImage src={imageUrl} {...styles.promoCategoryImage} />
                </Clickable>

                <Link
                    {...styles.tierLinkText}
                    href={`#${titleText}`}
                    data-test-selector={`categoryDetailsPromoProductLink_${promoPriceLabel}`}
                >
                    <Typography {...styles.promoCategorySelection}>{translate(promoPriceLabel)}</Typography>
                </Link>
            </StyledWrapper>
        );
    };

    return (
        <StyledWrapper {...styles.categoryCardWrapper}>
            <IconYellowAccentCard>
                <Icon {...styles.iconYellowAccentCard} src={YellowAccentLines} size={40} />
            </IconYellowAccentCard>
            {renderCategoryCard()}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(PromoCategorySelection),
    definition: {
        group: "Common",
        icon: "List",
        displayName: "Promo Category Selection",
        isSystem: true,
        fieldDefinitions: [
            {
                name: fields.titleText,
                displayName: "Anchor value",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                sortOrder: 10,
            },
            {
                name: fields.promoPriceLabel,
                displayName: "Promo Price Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                isRequired: true,
                tooltip: "",
                sortOrder: 20,
            },
            {
                name: fields.imageUrl,
                displayName: "Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                fieldType: "Translatable",
                sortOrder: 30,
            },
        ],
    },
};

export default widgetModule;
